import React, { useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@magoosh/lib/icons';
import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  onDeviceChange?: (deviceId: string) => void;
}

const NUM_UNITS = 15;
const ACTIVE_COLOR = colors.brandSecondary;
const INACTIVE_COLOR = '#e0e0e0';

export default (props: Props) => {
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('default');

  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const unitRefsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    refreshDevices();
  }, []);

  const refreshDevices = () => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const audioInputs = devices.filter((d) => d.kind === 'audioinput');
      setMicDevices(audioInputs);

      const saved = localStorage.getItem('audioInputDeviceId');
      if (saved && audioInputs.some((d) => d.deviceId === saved)) {
        setSelectedDeviceId(saved);
      }
    });
  };

  useEffect(() => {
    startMicStream();
    return () => stopMicStream();
  }, [selectedDeviceId]);

  const drawLevel = () => {
    animationFrameRef.current = requestAnimationFrame(drawLevel);

    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.fftSize);
    analyserRef.current.getByteTimeDomainData(dataArray);
    const rms = Math.sqrt(
      dataArray.reduce((sum, v) => sum + ((v - 128) / 128) ** 2, 0) / dataArray.length
    );

    const fillCount = Math.min(Math.round(rms * NUM_UNITS * 4), NUM_UNITS);

    unitRefsRef.current.forEach((el, i) => {
      if (!el) return;
      el.style.backgroundColor = i < fillCount ? ACTIVE_COLOR : INACTIVE_COLOR;
    });
  };

  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    localStorage.setItem('audioInputDeviceId', deviceId);
    props.onDeviceChange?.(deviceId);
  };

  const startMicStream = async () => {
    stopMicStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedDeviceId === 'default' ? true : { deviceId: { exact: selectedDeviceId } },
        video: false
      });
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      drawLevel();
    } catch {
      // mic access denied or unavailable
    }
  };

  const stopMicStream = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    analyserRef.current = null;
  };

  return (
    <div className="d-flex flex-column gap-m">
      {
        <div className="d-flex flex-column gap-xs">
          <div className="align-items-center d-flex gap-xs small">
            Input Device
            <a onClick={refreshDevices} style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon className="text-gray-light tiny" icon="refresh" />
            </a>
          </div>

          <select
            id="input-device-select"
            className="form-control"
            value={selectedDeviceId}
            onChange={(e) => handleDeviceChange(e.target.value)}
          >
            {
              (micDevices || []).map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                </option>
              ))
            }
          </select>
        </div>
      }

      <div className="d-flex flex-column gap-xs">
        <div className="small">Test Your Microphone</div>

        <div className="d-flex gap-xs">
          {
            Array.from({ length: NUM_UNITS }, (_, i) => (
              <div
                key={i}
                ref={(el) => { unitRefsRef.current[i] = el; }}
                style={{
                  width: 12,
                  height: 24,
                  borderRadius: 2,
                  backgroundColor: INACTIVE_COLOR
                }}
              />
            ))
          }
        </div>
      </div>
    </div>
  );
};
